import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, CATEGORY_LABELS, t, roleLabel } from "@/lib/api";
import { PageHeader } from "@/components/Common";

const PALETTE = ["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];

const Kpi = ({ label, value, hint, testid }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-3 font-display text-4xl font-bold">{value}</div>
    <div className="mt-1 text-xs text-slate-500">{hint}</div>
  </div>
);

const Card = ({ title, children, className = "", testid }) => (
  <div className={`rounded-3xl border border-slate-200 bg-white p-6 ${className}`} data-testid={testid}>
    <h3 className="font-display text-lg font-semibold">{title}</h3>
    <div className="mt-4 h-64">{children}</div>
  </div>
);

function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  const setRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success(t("Rôle mis à jour", "Role updated")); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="hr-users-list">
      {users.map((u) => (
        <div key={u.user_id} className="flex items-center gap-4 px-6 py-3 text-sm">
          <div className="flex-1"><div className="font-semibold">{u.name}</div><div className="text-xs text-slate-500">{u.email} · {u.department}</div></div>
          {user.role === "admin" && u.user_id !== user.user_id ? (
            <select data-testid={`hr-user-role-${u.user_id}`} value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-sm">
              <option value="employee">{roleLabel("employee")}</option><option value="manager">Manager</option><option value="admin">Admin</option>
            </select>
          ) : <span className="text-xs text-slate-500">{roleLabel(u.role)}</span>}
        </div>
      ))}
    </div>
  );
}

export default function HRDashboard() {
  const [dept, setDept] = useState("");
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/hr/stats", { params: { department: dept || undefined } }).then(({ data }) => setS(data)); }, [dept]);
  if (!s) return null;

  const exportCsv = () => {
    const rows = [[t("Semaine", "Week"), t("Engagement %", "Engagement %"), t("Activités", "Activities"), t("Autonomie /5", "Autonomy /5"), t("Sens /5", "Meaning /5")], ...s.weekly.map((w) => [w.week, w.engagement, w.activities, w.autonomy ?? "", w.meaning ?? ""])];
    const blob = new Blob(["\ufeff" + rows.map((r) => r.join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `sparkmind-rh-${dept || "global"}.csv`; a.click();
  };

  return (
    <div>
      <PageHeader eyebrow={t("Mesure de l'impact", "Impact measurement")} title={t("Dashboard RH", "HR dashboard")} subtitle={t("Engagement, innovations concrétisées et sentiment d'autonomie des collaborateurs.", "Engagement, implemented innovations and employees' sense of autonomy.")}>
        <div className="flex gap-2">
          <select data-testid="hr-department-filter" value={dept} onChange={(e) => setDept(e.target.value)} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold">
            <option value="">{t("Tous les services", "All departments")}</option>{s.departments.map((d) => <option key={d}>{d}</option>)}
          </select>
          <button data-testid="hr-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 transition-colors"><Download className="h-4 w-4" /> CSV</button>
        </div>
      </PageHeader>
      <Tabs defaultValue="stats">
        <TabsList className="mb-6"><TabsTrigger value="stats" data-testid="hr-tab-stats">{t("Indicateurs", "Metrics")}</TabsTrigger><TabsTrigger value="users" data-testid="hr-tab-users">{t("Utilisateurs", "Users")}</TabsTrigger></TabsList>
        <TabsContent value="stats">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi testid="hr-kpi-engagement" label={t("Taux d'engagement", "Engagement rate")} value={`${s.engagement_rate}%`} hint={t(`${s.active_users}/${s.headcount} actifs sur 30 jours`, `${s.active_users}/${s.headcount} active over 30 days`)} />
            <Kpi testid="hr-kpi-implemented" label={t("Innovations mises en place", "Innovations implemented")} value={s.ideas_implemented} hint={t(`${s.ideas_validated} validées · ${s.ideas_total} soumises`, `${s.ideas_validated} approved · ${s.ideas_total} submitted`)} />
            <Kpi testid="hr-kpi-autonomy" label={t("Sentiment d'autonomie", "Sense of autonomy")} value={`${s.autonomy_avg}/5`} hint={t("Moyenne des météos hebdo", "Average of weekly check-ins")} />
            <Kpi testid="hr-kpi-response" label={t("Taux de réponse managers", "Manager response rate")} value={`${s.response_rate}%`} hint={t("Idées ayant reçu une décision", "Ideas that received a decision")} />
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <Card title={t("Engagement hebdomadaire (%)", "Weekly engagement (%)")} testid="rh-chart-engagement">
              <ResponsiveContainer><LineChart data={s.weekly}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="week" fontSize={12} /><YAxis fontSize={12} /><Tooltip />
                <Line type="monotone" dataKey="engagement" name={t("Engagement", "Engagement")} stroke="#6366F1" strokeWidth={3} dot={{ r: 4 }} /></LineChart></ResponsiveContainer>
            </Card>
            <Card title={t("Autonomie & sens ressentis (/5)", "Perceived autonomy & meaning (/5)")} testid="rh-chart-autonomy">
              <ResponsiveContainer><LineChart data={s.weekly}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="week" fontSize={12} /><YAxis domain={[1, 5]} fontSize={12} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="autonomy" name={t("Autonomie", "Autonomy")} stroke="#10B981" strokeWidth={3} connectNulls />
                <Line type="monotone" dataKey="meaning" name={t("Sens", "Meaning")} stroke="#F59E0B" strokeWidth={3} connectNulls /></LineChart></ResponsiveContainer>
            </Card>
            <Card title={t("Entonnoir des idées", "Idea funnel")} testid="rh-chart-ideas">
              <ResponsiveContainer><BarChart data={s.ideas_by_status}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="status" fontSize={11} /><YAxis allowDecimals={false} fontSize={12} /><Tooltip />
                <Bar dataKey="count" name={t("Idées", "Ideas")} radius={[8, 8, 0, 0]}>{s.ideas_by_status.map((_, i) => <Cell key={i} fill={PALETTE[i % 5]} />)}</Bar></BarChart></ResponsiveContainer>
            </Card>
            <Card title={t("Participation par méthode", "Participation by method")} testid="rh-chart-workshops">
              <ResponsiveContainer><PieChart><Pie data={s.workshops_by_method} dataKey="count" nameKey="workshop" innerRadius={50} outerRadius={90} paddingAngle={3}>
                {s.workshops_by_method.map((_, i) => <Cell key={i} fill={PALETTE[i % 5]} />)}</Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} /></PieChart></ResponsiveContainer>
            </Card>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-3" data-testid="hr-ideas-by-category">
            {s.ideas_by_category.map((c) => (
              <div key={c.category} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex justify-between"><span className="text-sm">{CATEGORY_LABELS[c.category]}</span><span className="font-mono font-semibold">{c.count}</span></div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
      </Tabs>
    </div>
  );
}
